import { EditOutlined, PlusOutlined, ReloadOutlined, SettingOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../api/errors';
import {
  createProjectType,
  listProjectTypes,
  setProjectTypeActive,
  updateProjectTypeDefaults,
  updateProjectTypeIdentity,
} from '../api/projects';
import type { ProjectType } from '../api/projects';
import { PermissionCodes } from '../auth/permissions';
import { useAuth } from '../auth/useAuth';

/** Giá trị của biểu mẫu khai và sửa tên loại dự án. */
interface IdentityFormValues {
  readonly code: string;
  readonly name: string;
  readonly nameEn: string | null;
  readonly description: string | null;
  readonly sortOrder: number;
}

/** Giá trị của biểu mẫu sửa giá trị mặc định. */
interface DefaultsFormValues {
  readonly codePrefix: string | null;
  readonly defaultDurationMonths: number | null;
  readonly defaultWarrantyMonths: number | null;
  readonly requiresVisionConfiguration: boolean;
  readonly requiresInstallationOnsite: boolean;
}

/** Dữ liệu của một trang đã nạp xong, kèm khóa nhận dạng lần nạp đó. */
interface LoadedPage {
  readonly key: string;
  readonly items: readonly ProjectType[];
  readonly totalCount: number;
}

/** Số bản ghi mỗi trang. Khớp với mặc định của máy chủ. */
const pageSize = 25;

/**
 * Trang danh mục loại dự án.
 *
 * <b>Không có nút xóa.</b> Loại dự án đã dùng cho dự án nào thì phải còn để những dự án đó tra ngược
 * được; loại chưa dùng thì xóa cũng chẳng lợi gì. Thứ thay thế là ngừng dùng, và nút ngừng dùng bị
 * vô hiệu hóa ngay trên bảng khi loại còn dự án chưa đóng — máy chủ đã đếm sẵn con số đó, nên người
 * dùng không phải bấm rồi mới biết là không được.
 *
 * <b>Hai cờ nghiệp vụ được hiển thị ngay trên bảng</b> dù chưa module nào dùng tới chúng. Chúng
 * quyết định những thứ có thật về sau — dự án cần lắp đặt tại hiện trường thì phải có nhật ký công
 * trường và chi phí công tác — nên để người khai danh mục nhìn thấy và điền đúng ngay từ đầu, thay
 * vì phải đi hỏi lại từng loại về sau.
 */
export function ProjectTypePage(): ReactNode {
  const { hasPermission } = useAuth();

  const canManage = hasPermission(PermissionCodes.ProjectTypeManage);

  const [identityForm] = Form.useForm<IdentityFormValues>();
  const [defaultsForm] = Form.useForm<DefaultsFormValues>();

  const [pageNumber, setPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  const [loaded, setLoaded] = useState<LoadedPage | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [editing, setEditing] = useState<ProjectType | null>(null);
  const [isIdentityOpen, setIsIdentityOpen] = useState(false);
  const [isDefaultsOpen, setIsDefaultsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const requestKey = useMemo(
    () => JSON.stringify({ searchTerm, pageNumber, reloadToken }),
    [searchTerm, pageNumber, reloadToken],
  );

  const isLoading = loaded?.key !== requestKey && errorMessage === null;

  useEffect(() => {
    let isCurrent = true;

    async function load(): Promise<void> {
      setErrorMessage(null);

      try {
        const page = await listProjectTypes({ searchTerm, pageNumber, pageSize });

        if (!isCurrent) {
          return;
        }

        setLoaded({ key: requestKey, items: page.items, totalCount: page.totalCount });
      } catch (error) {
        if (!isCurrent) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError ? error.message : 'Không tải được danh mục loại dự án.',
        );
      }
    }

    void load();

    return () => {
      isCurrent = false;
    };
  }, [requestKey, searchTerm, pageNumber]);

  const reload = useCallback(() => { setReloadToken((token) => token + 1); }, []);

  function openCreate(): void {
    setEditing(null);
    identityForm.resetFields();
    identityForm.setFieldsValue({ sortOrder: 0 });
    setIsIdentityOpen(true);
  }

  function openEditIdentity(projectType: ProjectType): void {
    setEditing(projectType);
    identityForm.setFieldsValue({
      code: projectType.code,
      name: projectType.name,
      nameEn: projectType.nameEn,
      description: projectType.description,
      sortOrder: projectType.sortOrder,
    });
    setIsIdentityOpen(true);
  }

  function openEditDefaults(projectType: ProjectType): void {
    setEditing(projectType);
    defaultsForm.setFieldsValue({
      codePrefix: projectType.codePrefix,
      defaultDurationMonths: projectType.defaultDurationMonths,
      defaultWarrantyMonths: projectType.defaultWarrantyMonths,
      requiresVisionConfiguration: projectType.requiresVisionConfiguration,
      requiresInstallationOnsite: projectType.requiresInstallationOnsite,
    });
    setIsDefaultsOpen(true);
  }

  async function submitIdentity(values: IdentityFormValues): Promise<void> {
    setIsSaving(true);

    try {
      if (editing === null) {
        await createProjectType({
          code: values.code,
          name: values.name,
          nameEn: values.nameEn ?? null,
          description: values.description ?? null,
          sortOrder: values.sortOrder,
        });

        message.success('Đã khai loại dự án mới.');
      } else {
        await updateProjectTypeIdentity(editing.id, {
          name: values.name,
          nameEn: values.nameEn ?? null,
          description: values.description ?? null,
          sortOrder: values.sortOrder,
        });

        message.success('Đã lưu thay đổi.');
      }

      setIsIdentityOpen(false);
      identityForm.resetFields();
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không lưu được.');
    } finally {
      setIsSaving(false);
    }
  }

  async function submitDefaults(values: DefaultsFormValues): Promise<void> {
    if (editing === null) {
      return;
    }

    setIsSaving(true);

    try {
      await updateProjectTypeDefaults(editing.id, {
        codePrefix: values.codePrefix ?? null,
        defaultDurationMonths: values.defaultDurationMonths ?? null,
        defaultWarrantyMonths: values.defaultWarrantyMonths ?? null,
        requiresVisionConfiguration: values.requiresVisionConfiguration,
        requiresInstallationOnsite: values.requiresInstallationOnsite,
      });

      message.success('Đã lưu giá trị mặc định.');
      setIsDefaultsOpen(false);
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không lưu được.');
    } finally {
      setIsSaving(false);
    }
  }

  async function toggleActive(projectType: ProjectType): Promise<void> {
    try {
      await setProjectTypeActive(projectType.id, !projectType.isActive);

      message.success(projectType.isActive ? 'Đã ngừng dùng.' : 'Đã cho dùng lại.');
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không đổi được trạng thái.');
    }
  }

  const columns: TableColumnsType<ProjectType> = [
    {
      title: 'Mã',
      dataIndex: 'code',
      width: 160,
      render: (code: string) => <Typography.Text strong>{code}</Typography.Text>,
    },
    {
      title: 'Tên loại dự án',
      dataIndex: 'name',
      render: (name: string, projectType) => (
        <Space direction="vertical" size={0}>
          <span>{name}</span>
          {projectType.description !== null && projectType.description !== '' ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {projectType.description}
            </Typography.Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Tiền tố mã',
      dataIndex: 'codePrefix',
      width: 110,
      render: (codePrefix: string | null) => codePrefix ?? '—',
    },
    {
      title: 'Thời gian điển hình',
      dataIndex: 'defaultDurationMonths',
      width: 150,
      render: (months: number | null) => (months === null ? '—' : `${months.toString()} tháng`),
    },
    {
      title: 'Bảo hành',
      dataIndex: 'defaultWarrantyMonths',
      width: 110,
      render: (months: number | null) => (months === null ? '—' : `${months.toString()} tháng`),
    },
    {
      title: 'Yêu cầu',
      key: 'requirements',
      width: 190,
      render: (_, projectType) => (
        <Space size={4} wrap>
          {projectType.requiresVisionConfiguration ? (
            <Tooltip title="Dự án loại này cần hồ sơ cấu hình thị giác: camera, ống kính, đèn, thuật toán, ngưỡng.">
              <Tag color="blue">Hồ sơ thị giác</Tag>
            </Tooltip>
          ) : null}
          {projectType.requiresInstallationOnsite ? (
            <Tooltip title="Dự án loại này có lắp đặt tại nhà máy khách, nên cần nhật ký công trường và chi phí công tác.">
              <Tag color="cyan">Lắp đặt hiện trường</Tag>
            </Tooltip>
          ) : null}
          {!projectType.requiresVisionConfiguration && !projectType.requiresInstallationOnsite
            ? '—'
            : null}
        </Space>
      ),
    },
    {
      title: 'Dự án đang mở',
      dataIndex: 'openProjectCount',
      width: 130,
      align: 'right',
      render: (count: number) => (count === 0 ? '—' : count),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      width: 120,
      render: (isActive: boolean) =>
        isActive ? <Tag color="success">Còn dùng</Tag> : <Tag>Đã ngừng</Tag>,
    },
    {
      title: '',
      key: 'actions',
      width: 250,
      render: (_, projectType) => (
        <Space size={4}>
          <Button
            size="small"
            icon={<EditOutlined />}
            disabled={!canManage}
            onClick={() => { openEditIdentity(projectType); }}
          >
            Sửa
          </Button>
          <Button
            size="small"
            icon={<SettingOutlined />}
            disabled={!canManage}
            onClick={() => { openEditDefaults(projectType); }}
          >
            Mặc định
          </Button>
          <Tooltip
            title={
              projectType.isActive && projectType.openProjectCount > 0
                ? `Còn ${projectType.openProjectCount.toString()} dự án chưa đóng thuộc loại này.`
                : ''
            }
          >
            <Button
              size="small"
              disabled={
                !canManage || (projectType.isActive && projectType.openProjectCount > 0)
              }
              onClick={() => void toggleActive(projectType)}
            >
              {projectType.isActive ? 'Ngừng dùng' : 'Dùng lại'}
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Loại dự án
        </Typography.Title>
        <Space>
          <Input.Search
            allowClear
            placeholder="Tìm theo mã hoặc tên"
            style={{ width: 260 }}
            onSearch={(value) => {
              setSearchTerm(value);
              setPageNumber(1);
            }}
          />
          <Button icon={<ReloadOutlined />} onClick={reload}>
            Tải lại
          </Button>
          <Button type="primary" icon={<PlusOutlined />} disabled={!canManage} onClick={openCreate}>
            Khai loại mới
          </Button>
        </Space>
      </Space>

      {errorMessage !== null ? <Alert type="error" showIcon message={errorMessage} /> : null}

      <Table<ProjectType>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={loaded?.items}
        loading={isLoading}
        pagination={{
          current: pageNumber,
          pageSize,
          total: loaded?.totalCount ?? 0,
          showSizeChanger: false,
          onChange: setPageNumber,
        }}
      />

      <Modal
        open={isIdentityOpen}
        title={editing === null ? 'Khai loại dự án mới' : `Sửa loại dự án ${editing.code}`}
        okText="Lưu"
        cancelText="Đóng"
        confirmLoading={isSaving}
        onCancel={() => { setIsIdentityOpen(false); }}
        onOk={() => { identityForm.submit(); }}
      >
        <Form form={identityForm} layout="vertical" onFinish={(values) => void submitIdentity(values)}>
          <Form.Item
            name="code"
            label="Mã loại dự án"
            rules={[{ required: true, message: 'Chưa nhập mã.' }]}
            extra="Chữ in hoa không dấu, chữ số, gạch nối và gạch dưới. Không sửa được sau khi khai."
          >
            <Input disabled={editing !== null} placeholder="AOI" />
          </Form.Item>
          <Form.Item
            name="name"
            label="Tên loại dự án"
            rules={[{ required: true, message: 'Chưa nhập tên.' }]}
          >
            <Input placeholder="Kiểm tra ngoại quan tự động" />
          </Form.Item>
          <Form.Item name="nameEn" label="Tên tiếng Anh">
            <Input placeholder="Automated Optical Inspection" />
          </Form.Item>
          <Form.Item name="description" label="Giải thích loại dự án này gồm những công việc gì">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="sortOrder" label="Thứ tự hiển thị" initialValue={0}>
            <InputNumber<number> min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={isDefaultsOpen}
        title={editing === null ? 'Giá trị mặc định' : `Giá trị mặc định — ${editing.code}`}
        okText="Lưu"
        cancelText="Đóng"
        confirmLoading={isSaving}
        onCancel={() => { setIsDefaultsOpen(false); }}
        onOk={() => { defaultsForm.submit(); }}
      >
        <Form form={defaultsForm} layout="vertical" onFinish={(values) => void submitDefaults(values)}>
          <Form.Item
            name="codePrefix"
            label="Tiền tố mã dự án"
            extra="Chỉ là gợi ý hiển thị khi mở dự án mới. Mã dự án hiện vẫn nhập tay."
          >
            <Input placeholder="AOI" />
          </Form.Item>
          <Form.Item
            name="defaultDurationMonths"
            label="Thời gian điển hình (tháng)"
            extra="Để trống nếu loại dự án này không có thời gian điển hình."
          >
            <InputNumber<number> min={0} max={60} step={0.5} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="defaultWarrantyMonths"
            label="Bảo hành mặc định (tháng)"
            extra="Số 0 nghĩa là loại dự án này không có bảo hành — khác với để trống."
          >
            <InputNumber<number> min={0} max={120} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="requiresVisionConfiguration"
            label="Cần hồ sơ cấu hình thị giác"
            valuePropName="checked"
            extra="Thiếu hồ sơ này thì khi hệ thống chạy sai sau một năm, không ai dựng lại được cấu hình ban đầu."
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="requiresInstallationOnsite"
            label="Có lắp đặt tại nhà máy khách"
            valuePropName="checked"
            extra="Quyết định dự án có cần nhật ký công trường và chi phí công tác hay không."
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
